import { useEffect, useMemo, useRef, useState } from 'react';
import { DecimalTextInput } from '@/components/common/DecimalTextInput';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Receipt,
  Loader2,
  CheckCircle2,
  XCircle,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  SlidersHorizontal,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { useDataService } from '@/lib/service-context';
import { Pagination } from '@/components/common/Pagination';
import { TabsBar } from '@/components/common/TabsBar';
import { formatTND } from '@/lib/format';
import type { CommandeStatus } from '@erp/domain/pro';

const fmtIntFR = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));

type SortKey = 'code' | 'date' | 'client' | 'pttc' | 'commission' | 'status';
type SortDir = 'asc' | 'desc';

const STATUS_LABELS: Record<CommandeStatus | 'all', string> = {
  all: 'Tous',
  en_attente: 'En attente',
  confirmee: 'Confirmée',
  emballee: 'Emballée',
  expediee: 'Expédié',
  livree: 'Livrée',
  retournee: 'Retournée',
  rejetee: 'Rejetée',
};

const TABS: (CommandeStatus | 'all')[] = [
  'all',
  'en_attente',
  'confirmee',
  'expediee',
  'emballee',
  'livree',
  'retournee',
  'rejetee',
];

function StatTile({
  icon: Icon,
  label,
  value,
  trendValue,
  positive,
}: {
  icon: typeof Receipt;
  label: string;
  value: number | string;
  trendValue: number;
  positive: boolean;
}) {
  return (
    <div className="pro-card p-4 flex items-center gap-3">
      <div
        className="rounded-[10px] flex items-center justify-center shrink-0 p-3"
        style={{ background: 'var(--pro-primary)' }}
      >
        <Icon className="w-[30px] h-[30px] text-white" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-[7px]">
        <div
          className="text-[14px] leading-[1.25]"
          style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
        >
          {label}
        </div>
        <div className="flex items-end justify-between gap-2">
          <div
            className="text-[24px] font-semibold leading-[1.1] truncate"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            {value}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span
              className="inline-flex items-center justify-center rounded-[12px] p-[2px]"
              style={{ background: 'var(--pro-primary-subtle)' }}
            >
              {positive ? (
                <ArrowUpRight className="w-[10px] h-[10px]" style={{ color: 'var(--pro-primary)' }} strokeWidth={2.5} />
              ) : (
                <ArrowDownRight className="w-[10px] h-[10px]" style={{ color: 'var(--pro-primary)' }} strokeWidth={2.5} />
              )}
            </span>
            <span
              className="text-[12px] leading-[1.3]"
              style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
            >
              {trendValue.toFixed(2).replace('.', ',')}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableTh({
  label,
  sortKey,
  current,
  dir,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey | null;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = current === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      className={`text-left py-3 px-3 text-[12px] font-medium select-none ${className ?? ''}`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-neutral-700"
        style={{ color: active ? 'var(--pro-text-dark)' : 'var(--pro-text-grey)' }}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    </th>
  );
}

export function OrdresListPage() {
  const { t } = useTranslation();
  const ds = useDataService();
  const navigate = useNavigate();
  const filterRef = useRef<HTMLDivElement | null>(null);
  const [tab, setTab] = useState<CommandeStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  useEffect(() => {
    if (!filterOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [filterOpen]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allQuery = useQuery({
    queryKey: ['pro-commandes-list-all'],
    queryFn: () => ds.proCommandes.list(),
  });
  const list = allQuery.data ?? [];

  const filtered = useMemo(() => {
    let l = list;
    if (tab !== 'all') l = l.filter((c) => c.status === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter(
        (c) =>
          c.code.toLowerCase().includes(q) ||
          c.client?.nom?.toLowerCase().includes(q) ||
          c.client?.prenom?.toLowerCase().includes(q),
      );
    }
    if (dateFrom) l = l.filter((c) => c.date >= dateFrom);
    if (dateTo) l = l.filter((c) => c.date <= dateTo + 'T23:59:59');
    const min = amountMin ? Number(amountMin) : null;
    const max = amountMax ? Number(amountMax) : null;
    if (min !== null && !isNaN(min)) l = l.filter((c) => c.pttc >= min);
    if (max !== null && !isNaN(max)) l = l.filter((c) => c.pttc <= max);
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const get = (c: (typeof l)[number]) => {
        switch (sortKey) {
          case 'code':
            return c.code;
          case 'date':
            return c.date;
          case 'client':
            return [c.client?.prenom, c.client?.nom].filter(Boolean).join(' ').toLowerCase();
          case 'pttc':
            return c.pttc;
          case 'commission':
            return c.totalCommission ?? 0;
          case 'status':
            return c.status;
        }
      };
      l = [...l].sort((a, b) => {
        const va = get(a);
        const vb = get(b);
        if (va == null) return 1;
        if (vb == null) return -1;
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    }
    return l;
  }, [list, tab, search, sortKey, sortDir, dateFrom, dateTo, amountMin, amountMax]);

  const activeFilterCount =
    (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (amountMin ? 1 : 0) + (amountMax ? 1 : 0);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Figma buckets: total / en_cours_processus / complete / annule
  const counts = useMemo(() => {
    let enCours = 0;
    let complete = 0;
    let annule = 0;
    for (const c of list) {
      if (
        c.status === 'en_attente' ||
        c.status === 'confirmee' ||
        c.status === 'emballee' ||
        c.status === 'expediee'
      )
        enCours++;
      else if (c.status === 'livree') complete++;
      else if (c.status === 'rejetee' || c.status === 'retournee') annule++;
    }
    return { total: list.length, enCours, complete, annule };
  }, [list]);

  // Period filter for the Aperçu chart
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'year' | 'all'>('week');
  const [chartPeriodOpen, setChartPeriodOpen] = useState(false);

  // Weekly chart data (Mon..Sun) for the chosen period.
  const weekly = useMemo(() => {
    const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    const counts = [0, 0, 0, 0, 0, 0, 0];
    const now = new Date();
    let cutoff: Date | null = null;
    if (chartPeriod === 'week') {
      // Start of current ISO week (Monday).
      const d = new Date(now);
      const dow = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - dow);
      d.setHours(0, 0, 0, 0);
      cutoff = d;
    } else if (chartPeriod === 'month') {
      cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (chartPeriod === 'year') {
      cutoff = new Date(now.getFullYear(), 0, 1);
    }
    for (const c of list) {
      const d = new Date(c.date);
      if (cutoff && d < cutoff) continue;
      const dow = (d.getDay() + 6) % 7;
      counts[dow]!++;
    }
    return days.map((day, i) => ({ day, value: counts[i] ?? 0 }));
  }, [list, chartPeriod]);

  const PERIOD_LABELS: Record<typeof chartPeriod, string> = {
    week: t('pro.ordres.period_week', 'Cette semaine'),
    month: t('pro.ordres.period_month', 'Ce mois'),
    year: t('pro.ordres.period_year', 'Cette année'),
    all: t('pro.ordres.period_all', 'Tout'),
  };

  const fmtDateMain = (s: string) => {
    try {
      return new Date(s).toISOString().slice(0, 10);
    } catch {
      return s;
    }
  };
  const fmtDateTime = (s: string) => {
    try {
      const d = new Date(s);
      return `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  const statusClass = (s: CommandeStatus): string => {
    switch (s) {
      case 'en_attente':
        return 'pro-status-pending';
      case 'confirmee':
        return 'pro-status-confirmed';
      case 'emballee':
        return 'pro-status-packed';
      case 'expediee':
        return 'pro-status-shipped';
      case 'livree':
        return 'pro-status-delivered';
      case 'retournee':
        return 'pro-status-returned';
      case 'rejetee':
        return 'pro-status-rejected';
      default:
        return 'pro-status-cancelled';
    }
  };

  return (
    <div className="space-y-4">
      {/* Section: stats (left) + chart (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatTile
            icon={Receipt}
            label={t('pro.ordres.stat_total', 'Total des commandes')}
            value={fmtIntFR(counts.total)}
            trendValue={1.58}
            positive
          />
          <StatTile
            icon={Loader2}
            label={t('pro.ordres.stat_en_cours', 'En cours de processus')}
            value={fmtIntFR(counts.enCours)}
            trendValue={0.42}
            positive={false}
          />
          <StatTile
            icon={CheckCircle2}
            label={t('pro.ordres.stat_complete', 'Complété')}
            value={fmtIntFR(counts.complete)}
            trendValue={2.36}
            positive
          />
          <StatTile
            icon={XCircle}
            label={t('pro.ordres.stat_annule', 'Annulé')}
            value={fmtIntFR(counts.annule)}
            trendValue={1.12}
            positive={false}
          />
        </div>

        {/* Chart card */}
        <div className="lg:col-span-7 pro-card p-5">
          <div className="flex items-start justify-between mb-2">
            <h2
              className="text-[16px] font-semibold"
              style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
            >
              {t('pro.ordres.overview', 'Aperçu des commandes')}
            </h2>
            <div className="relative">
              <button
                type="button"
                onClick={() => setChartPeriodOpen((o) => !o)}
                className="flex items-center gap-1 text-[13px] px-2 py-1.5 rounded-lg"
                style={{
                  color: 'var(--pro-text-grey)',
                  background: chartPeriodOpen ? 'var(--pro-grey-subtle)' : 'transparent',
                }}
              >
                {PERIOD_LABELS[chartPeriod]}
                <ChevronDown className="w-4 h-4" />
              </button>
              {chartPeriodOpen && (
                <div
                  className="absolute right-0 mt-1 w-44 rounded-lg z-20 py-1 pro-scale-in"
                  style={{
                    background: 'white',
                    border: '1px solid var(--pro-border)',
                    boxShadow: '0 8px 24px rgba(13,13,18,0.08)',
                  }}
                >
                  {(['week', 'month', 'year', 'all'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        setChartPeriod(p);
                        setChartPeriodOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-[13px]"
                      style={{
                        color:
                          p === chartPeriod ? 'var(--pro-primary)' : 'var(--pro-text-dark)',
                        background:
                          p === chartPeriod ? 'var(--pro-primary-subtle)' : 'transparent',
                        fontFamily: 'Figtree',
                      }}
                    >
                      {PERIOD_LABELS[p]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weekly}>
                <defs>
                  <linearGradient id="ordresGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B1E" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#FF6B1E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEB" vertical={false} />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#858585', fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#858585', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#333',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 12,
                    color: '#fff',
                  }}
                  formatter={(v) => [`${v} ordres`, '']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#FF6B1E"
                  strokeWidth={2}
                  fill="url(#ordresGrad)"
                  dot={false}
                  activeDot={{ r: 5, fill: '#FF6B1E' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Toolbar + table */}
      <div className="pro-card p-5 space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsBar
            tabs={TABS.map((k) => ({
              key: k,
              label: t(`pro.ordres.filter_${k}`, STATUS_LABELS[k]),
            }))}
            value={tab}
            onChange={(k) => {
              setTab(k);
              setPage(1);
            }}
          />
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div ref={filterRef} className="relative flex-1 sm:flex-none">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: 'var(--pro-text-grey)' }}
              />
              <input
                type="search"
                placeholder={t('pro.ordres.search_placeholder', 'Rechercher un identifiant, un client...')}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10 pr-3 h-9 rounded-lg text-[13px] w-full sm:w-[260px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                style={{ background: 'var(--pro-grey-subtle)', color: 'var(--pro-text-dark)' }}
              />
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((o) => !o)}
                aria-label={t('pro.ordres.filters', 'Filtres')}
                aria-expanded={filterOpen}
                className="w-9 h-9 rounded-lg flex items-center justify-center relative"
                style={{
                  border: '1px solid var(--pro-border)',
                  color: 'var(--pro-primary)',
                  background: filterOpen ? 'var(--pro-primary-subtle)' : 'white',
                }}
              >
                <SlidersHorizontal className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full text-[10px] font-bold flex items-center justify-center text-white px-1"
                    style={{ background: 'var(--pro-primary)' }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {filterOpen && (
                <div
                  className="absolute right-0 mt-2 w-[320px] rounded-xl z-20 p-4 pro-scale-in"
                  style={{
                    background: 'white',
                    border: '1px solid var(--pro-border)',
                    boxShadow: '0 12px 32px rgba(13,13,18,0.10)',
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-[14px] font-semibold"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {t('pro.ordres.filters', 'Filtres')}
                    </span>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setDateFrom('');
                          setDateTo('');
                          setAmountMin('');
                          setAmountMax('');
                          setPage(1);
                        }}
                        className="text-[12px] font-medium"
                        style={{ color: 'var(--pro-primary)' }}
                      >
                        {t('pro.ordres.reset_filters', 'Réinitialiser')}
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label
                        className="text-[12px] font-medium mb-1.5 block"
                        style={{ color: 'var(--pro-text-grey)' }}
                      >
                        {t('pro.ordres.filter_date_range', 'Plage de dates')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => {
                            setDateFrom(e.target.value);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => {
                            setDateTo(e.target.value);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        className="text-[12px] font-medium mb-1.5 block"
                        style={{ color: 'var(--pro-text-grey)' }}
                      >
                        {t('pro.ordres.filter_amount_range', 'Montant TTC (DT)')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <DecimalTextInput
                          maxDecimals={3}
                          placeholder={t('pro.ordres.min', 'Min')}
                          value={amountMin}
                          onTextChange={(s) => {
                            setAmountMin(s);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                        <DecimalTextInput
                          maxDecimals={3}
                          placeholder={t('pro.ordres.max', 'Max')}
                          value={amountMax}
                          onTextChange={(s) => {
                            setAmountMax(s);
                            setPage(1);
                          }}
                          className="h-9 px-2 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                          style={{
                            border: '1px solid var(--pro-border)',
                            color: 'var(--pro-text-dark)',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div key={tab} className="pro-fade-in space-y-5">
        {allQuery.isLoading ? (
          <div className="p-12 text-center" style={{ color: 'var(--pro-text-grey)' }}>
            …
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Package
              className="w-10 h-10 mx-auto mb-2"
              style={{ color: 'var(--pro-text-grey-light)' }}
            />
            <p style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.ordres.empty', 'Aucune commande trouvée')}
            </p>
          </div>
        ) : (
          <>
          {/* Mobile: stacked cards (the 7-column table can't fit a phone) */}
          <div className="md:hidden space-y-3">
            {paged.map((c) => (
              <Link
                key={c.code}
                to={`/ordres/${encodeURIComponent(c.code)}`}
                className="block rounded-xl p-4 space-y-3 active:bg-orange-50/40"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-medium" style={{ color: 'var(--pro-text-grey)' }}>
                    ORD{c.code}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium shrink-0 ${statusClass(c.status)}`}
                  >
                    {STATUS_LABELS[c.status]}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-[14px] font-semibold truncate"
                    style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                  >
                    {[c.client?.prenom, c.client?.nom].filter(Boolean).join(' ') || '—'}
                  </span>
                  <span className="text-[12px] shrink-0" style={{ color: 'var(--pro-text-grey)' }}>
                    {fmtDateMain(c.date)} · {fmtDateTime(c.date)}
                  </span>
                </div>
                <div
                  className="flex items-center justify-between gap-2 pt-3"
                  style={{ borderTop: '1px dashed var(--pro-line)' }}
                >
                  <div>
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>Total TTC</div>
                    <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(c.pttc)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>Commission</div>
                    <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-primary)' }}>
                      {formatTND(c.totalCommission ?? 0)}
                    </div>
                  </div>
                </div>
                <span
                  className="flex w-full items-center justify-center px-3 py-2 rounded-lg text-[12px] font-semibold"
                  style={{
                    background: 'var(--pro-primary-subtle)',
                    color: 'var(--pro-text-dark)',
                    fontFamily: 'Figtree',
                  }}
                >
                  {t('pro.ordres.view', 'Voir la commande')}
                </span>
              </Link>
            ))}
          </div>
          {/* Desktop: full table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                  <SortableTh label="ID de commande" sortKey="code" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Date" sortKey="date" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Électricien" sortKey="client" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Total TTC" sortKey="pttc" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Commission" sortKey="commission" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Statut" sortKey="status" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {paged.map((c) => (
                  <tr
                    key={c.code}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/ordres/${encodeURIComponent(c.code)}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/ordres/${encodeURIComponent(c.code)}`);
                      }
                    }}
                    style={{ borderBottom: '1px solid var(--pro-line)' }}
                    className="cursor-pointer hover:bg-orange-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
                  >
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-grey)' }}>
                      ORD{c.code}
                    </td>
                    <td className="py-3 px-3">
                      <div style={{ color: 'var(--pro-text-dark)' }}>{fmtDateMain(c.date)}</div>
                      <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                        {fmtDateTime(c.date)}
                      </div>
                    </td>
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-dark)' }}>
                      {[c.client?.prenom, c.client?.nom].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-dark)' }}>
                      {formatTND(c.pttc)}
                    </td>
                    <td className="py-3 px-3 font-semibold" style={{ color: 'var(--pro-primary)' }}>
                      {formatTND(c.totalCommission ?? 0)}
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${statusClass(c.status)}`}
                      >
                        {STATUS_LABELS[c.status]}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <Link
                        to={`/ordres/${encodeURIComponent(c.code)}`}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-[12px] font-semibold"
                        style={{
                          background: 'var(--pro-primary-subtle)',
                          color: 'var(--pro-text-dark)',
                          fontFamily: 'Figtree',
                        }}
                      >
                        {t('pro.ordres.view', 'Voir la commande')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
        </div>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="text-left py-3 px-3 text-[12px] font-medium"
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}
